// Port of ios/Sources/Models/ScheduledPost.swift + the `scheduledWhenLabel` function beside
// ios/Sources/Views/ScheduleMessageSheet.swift (Kotlin twins: `ScheduledPost.kt`,
// `ScheduledWhenLabel.kt`).
//
// A message the SERVER is holding and will post at `scheduledAt` whether or not any client is
// running (shared/API.md §35). Decoded from the server's object and it keeps the raw JSON: `PUT
// /posts/schedule/{id}` refuses a partial body (measured), so a reschedule sends the object back
// with only the field it changes replaced. Re-encoding a modelled struct would quietly drop
// whatever this client does not model — exactly what a newer server adds.
import { tf } from '../i18n/i18n';
import { MessageTimestamp, type MessageTimestampOptions } from './messageTimestamp';
import { dayKind, dayMonthLabel, weekdayLabel } from './scheduleOptions';

export interface ScheduledPost {
  id: string;
  userId: string;
  channelId: string;
  rootId: string;
  message: string;
  scheduledAt: number;
  /** The server's report on a delivery it already tried: non-empty means it will never arrive. */
  errorCode: string;
  /** The object exactly as it arrived, as a JSON string (the store's own column). */
  rawJson: string;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

export function decodeScheduledPost(json: Record<string, unknown>): ScheduledPost {
  const at = json.scheduled_at;
  return {
    id: str(json.id),
    userId: str(json.user_id),
    channelId: str(json.channel_id),
    rootId: str(json.root_id),
    message: str(json.message),
    scheduledAt: typeof at === 'number' && Number.isFinite(at) ? Math.trunc(at) : 0,
    errorCode: str(json.error_code),
    rawJson: JSON.stringify(json),
  };
}

/**
 * The body a reschedule PUTs back: the server's own object with the time replaced, or null if
 * what was cached is not parseable any more. Null rather than a rebuilt object on purpose —
 * sending a body this client invented is how a priority silently disappears.
 */
export function rescheduledBody(rawJson: string, atMillis: number): Record<string, unknown> | null {
  let object: unknown;
  try {
    object = JSON.parse(rawJson);
  } catch {
    return null;
  }
  if (object == null || typeof object !== 'object' || Array.isArray(object)) return null;
  return { ...(object as Record<string, unknown>), scheduled_at: atMillis };
}

/**
 * The server's list is an object keyed by TEAM id plus a `directChannels` bucket (§35). Each entry
 * is paired with the team it came under; DMs belong to no team ('').
 */
export function flattenScheduledList(body: unknown, teamId: string): Array<{ post: ScheduledPost; teamId: string }> {
  if (body == null || typeof body !== 'object' || Array.isArray(body)) return [];
  const out: Array<{ post: ScheduledPost; teamId: string }> = [];
  for (const [bucket, list] of Object.entries(body as Record<string, unknown>)) {
    if (!Array.isArray(list)) continue;
    const owner = bucket === 'directChannels' ? '' : bucket === teamId ? teamId : bucket;
    for (const item of list) {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const post = decodeScheduledPost(item as Record<string, unknown>);
        if (post.id) out.push({ post, teamId: owner });
      }
    }
  }
  return out;
}

/**
 * "Mañana a las 09:00", "lun a las 14:30", "21 sep a las 08:00" — when a scheduled message will go
 * out, in the words a person would use. ONE function, because the sheet that CHOOSES the time and
 * the list that shows what was chosen have to read identically. The clock goes through
 * `MessageTimestamp`, so a reader outside Mexico gets the workspace's own time in parentheses.
 */
export function scheduledWhenLabel(atMillis: number, now: number, options: MessageTimestampOptions = {}): string {
  const parts = MessageTimestamp.parts(atMillis, options);
  const clock = parts.home != null ? tf('channel.time.withHomeZone', parts.local, parts.home) : parts.local;
  const zone = options.deviceZone;
  switch (dayKind(atMillis, now, zone)) {
    case 'today':
      return tf('schedule.when.today', clock);
    case 'tomorrow':
      return tf('schedule.when.tomorrow', clock);
    case 'withinAWeek':
      return tf('schedule.when.day', weekdayLabel(atMillis, zone), clock);
    case 'later':
      return tf('schedule.when.day', dayMonthLabel(atMillis, zone), clock);
  }
}
